/**
 * Zentrale Telefonnummer-Normalisierung für WhatsApp & Co.
 *
 * wa.me erwartet die Nummer im internationalen Format OHNE
 * führendes "+" oder "00" und OHNE Leerzeichen/Klammern/etc.
 *
 * Beispiele:
 *   01786354292         -> 491786354292
 *   +49 178 6354292     -> 491786354292
 *   0049 178 6354292    -> 491786354292
 *   49 1786354292       -> 491786354292
 *   +491786354292       -> 491786354292
 */

const DEFAULT_COUNTRY = "49"; // Deutschland — wird verwendet, wenn Nummer mit "0" beginnt

/**
 * Bereinigt eine Telefonnummer und konvertiert ins WhatsApp-Format
 * (digits-only, kein "+", kein "00", keine führende "0").
 *
 * @param {string} raw
 * @returns {string} bereinigte Nummer (z.B. "491786354292") oder "" wenn ungültig
 */
export const normalizePhoneForWhatsApp = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (!s) return "";

  // Behalte nur Ziffern + ein eventuell führendes "+"
  // Erst "+" am Anfang merken, dann alles Non-Digit entfernen.
  const hasPlus = s.startsWith("+");
  const digits = s.replace(/\D+/g, "");
  if (!digits) return "";

  // Case 1: kam mit "+" -> internationale Nummer ohne führende 0
  if (hasPlus) return digits;

  // Case 2: "00<country>..." -> entferne "00"
  if (digits.startsWith("00")) return digits.slice(2);

  // Case 3: "0<rest>" -> deutsche Nummer im nationalen Format -> Default-Country voranstellen
  if (digits.startsWith("0")) return DEFAULT_COUNTRY + digits.slice(1);

  // Case 4: bereits "49…" oder andere Country-Code — unverändert
  return digits;
};

/**
 * Lockere Validierung: WhatsApp erlaubt ~7–15 Digits.
 * Wir nehmen 8 als Untergrenze (kürzeste plausible Mobilnummer mit CC).
 *
 * @param {string} normalized
 * @returns {boolean}
 */
export const isValidWhatsAppNumber = (normalized) => {
  if (!normalized) return false;
  return /^\d{8,15}$/.test(normalized);
};

/**
 * Erzeugt eine sichere wa.me-URL.
 * @param {string} phone roh oder bereits bereinigt
 * @param {string=} message optional, wird URL-encoded
 * @returns {string|null} URL oder null wenn ungültig
 */
export const buildWhatsAppUrl = (phone, message) => {
  const normalized = normalizePhoneForWhatsApp(phone);
  if (!isValidWhatsAppNumber(normalized)) return null;
  const base = `https://wa.me/${normalized}`;
  if (message) {
    return `${base}?text=${encodeURIComponent(message)}`;
  }
  return base;
};

/**
 * Öffnet WhatsApp in einem neuen Tab.
 * Loggt zum Debugging Original/Bereinigt/URL (keine Lead-Daten beyond phone).
 * Gibt false zurück, wenn die Nummer ungültig ist.
 *
 * @param {string} phone
 * @param {string=} message
 * @returns {boolean} true wenn geöffnet, false wenn ungültig
 */
export const openWhatsApp = (phone, message) => {
  const normalized = normalizePhoneForWhatsApp(phone);
  const url = buildWhatsAppUrl(phone, message);
  console.log("[WhatsApp] Original:", phone);
  console.log("[WhatsApp] Bereinigt:", normalized);
  console.log("[WhatsApp] URL:", url);
  if (!url) return false;
  window.open(url, "_blank", "noopener,noreferrer");
  return true;
};
